// RITA-1602 — Lädt den Text der Handbücher erst, wenn er gebraucht wird: Jedes Handbuch ist ein eigenes Paket, der erste
// Aufruf des PRM lädt nichts davon. Einmal geladen, bleibt der Text für die Sitzung im Speicher; ein Fehlschlag (zum
// Beispiel ein Paket, das nach einem neuen Release nicht mehr da ist) wird nicht gemerkt und lässt sich wiederholen.
// Alte Antworten überschreiben nichts: Der Fehlerzustand gilt immer für die zuletzt angeforderten Handbücher.
import { useCallback, useEffect, useMemo, useState } from 'react';
import { HandbuchInhalt } from '../model/handbuchInhalt';
import { Handbuchtexte } from '../model/handbuchSuche';

const geladen = new Map<string, HandbuchInhalt>();
const laufend = new Map<string, Promise<HandbuchInhalt>>();

export type HandbuchLader = (handbuchId: string) => Promise<HandbuchInhalt>;

const standardLader: HandbuchLader = (handbuchId) =>
  import(/* webpackChunkName: "handbuch-[request]" */ `../inhalt/texte/${handbuchId}.json`).then(
    (modul) => (modul.default ?? modul) as HandbuchInhalt,
  );

function textLaden(handbuchId: string, lader: HandbuchLader): Promise<HandbuchInhalt> {
  const vorhanden = laufend.get(handbuchId);
  if (vorhanden) return vorhanden;
  const anfrage = lader(handbuchId)
    .then((inhalt) => {
      geladen.set(handbuchId, inhalt);
      return inhalt;
    })
    .finally(() => laufend.delete(handbuchId));
  laufend.set(handbuchId, anfrage);
  return anfrage;
}

/** Nur für Tests: Speicher leeren, damit jeder Fall frisch lädt. */
export function handbuchTexteVergessen(): void {
  geladen.clear();
  laufend.clear();
}

export type TexteStatus = 'bereit' | 'laedt' | 'fehler';

export interface HandbuchTexte {
  texte: Handbuchtexte;
  status: TexteStatus;
  neuLaden: () => void;
}

export default function useHandbuchTexte(
  handbuchIds: readonly string[],
  lader: HandbuchLader = standardLader,
): HandbuchTexte {
  const schluessel = handbuchIds.join('|');
  const [neuGeladen, setNeuGeladen] = useState(0);
  const [fehlerBei, setFehlerBei] = useState<string | null>(null);
  const [versuch, setVersuch] = useState(0);

  useEffect(() => {
    let aktuell = true;
    setFehlerBei(null);
    schluessel
      .split('|')
      .filter((id) => id && !geladen.has(id))
      .forEach((id) => {
        textLaden(id, lader).then(
          () => {
            if (aktuell) setNeuGeladen((alt) => alt + 1);
          },
          () => {
            if (aktuell) setFehlerBei(schluessel);
          },
        );
      });
    return () => {
      aktuell = false;
    };
  }, [schluessel, versuch, lader]);

  const neuLaden = useCallback(() => setVersuch((alt) => alt + 1), []);
  // `neuGeladen` zeigt an, dass im Speicher etwas dazugekommen ist.
  const texte = useMemo(() => {
    const ergebnis: Record<string, HandbuchInhalt | undefined> = {};
    schluessel.split('|').forEach((id) => {
      ergebnis[id] = geladen.get(id);
    });
    return ergebnis;
  }, [schluessel, neuGeladen]);
  const vollstaendig = Object.values(texte).every(Boolean);

  let status: TexteStatus = 'laedt';
  if (vollstaendig) status = 'bereit';
  else if (fehlerBei === schluessel) status = 'fehler';
  return { texte, status, neuLaden };
}
